import _ from 'lodash'
import KeenTracking from 'keen-tracking'
import {addAnalytic} from '../lib/api'
import slugify from './slugify'
import {
  Ballot,
  ParentCategory,
  SubCategory,
  InterstitialAd,
  CategoryAd,
  SponsorshipAd,
} from '../types/ballot'

// Track errors and messages in the dev console
(KeenTracking as any).debug = process.env.NEXT_PUBLIC_VERCEL_ENV === 'development'

// Disable event writes to the API
//KeenTracking.enabled = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'

export const keenClient = new KeenTracking({
  projectId: process.env.NEXT_PUBLIC_KEEN_PROJECT_ID as string,
  writeKey: process.env.NEXT_PUBLIC_KEEN_WRITE_KEY as string,
})

if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'development') {
  (keenClient as any).on('recordEvent', (KeenTracking as any).log)
}

// NOTE: Until we re-implement initAutoTracking, autoTrackingConfig is
// export const autoTrackingConfig = {
//   recordPageViews: true,
//   recordPageViewsOnExit: false,
//   recordScrollState: false,
//   recordClicks: false,
//   recordFormSubmits: false,
//   ignoreDisabledFormFields: true,
//   recordElementViews: false
// }

interface AdWithName {
  _id: string
  name: string
}

type QuestionDestination = SubCategory & {
  type: 'question'
  groupSlug: string
  slug: string
}

type AdDestination = InterstitialAd & {
  type: 'ad'
}

type Destination = QuestionDestination | AdDestination

type ComponentName = 'BallotShow' | 'GroupIndex' | 'GroupShow' | 'QuestionShow'

interface RecordAdAndPageViewsParams {
  ballotId: string | undefined | null
  group?: ParentCategory | null
  destination?: Destination | null
  linkId?: string | null
  displayAds?: (CategoryAd | AdWithName)[] | null
  footerAds?: (SponsorshipAd | AdWithName)[] | null
  sponsorshipAds?: (SponsorshipAd | AdWithName)[] | null
  componentName: ComponentName
  ballot: Ballot | null | undefined
}

interface RecordedAd {
  ad_id: string
  ad_name: string
  ad_view_type: 'DisplayAdView' | 'FooterAdView' | 'InterstitialAdView' | 'SponsorshipAdView'
}

export const recordAdAndPageViews = ({
  ballotId,
  group,
  destination,
  linkId,
  displayAds,
  footerAds,
  sponsorshipAds,
  componentName,
  ballot,
}: RecordAdAndPageViewsParams): void => {
  if (!ballotId || process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') return

  let pagename: string | null = null
  switch (componentName) {
  case 'BallotShow':
    pagename = '/'
    break
  case 'GroupIndex':
    pagename = '/groups'
    break
  case 'GroupShow':
    pagename = `/${slugify(group?.parent_category_name)}`
    break
  case 'QuestionShow':
    if (destination?.type === 'question') {
      pagename = `/${destination.groupSlug}/${destination.slug}`
    }
    break
  default:
    break
  }

  const ballotDetails = {
    ballotId: ballotId,
    categoryId: group?._id || null,
    categoryName: _.trim(group?.parent_category_name) || null,
    chosenSubCategoryId: destination?.type === 'question' ? destination._id : null,
    subCategoryName: destination?.type === 'question' ? _.trim(destination.name) : null,
    linkId: linkId || null,
  }
  const internalAnalyticsIsDisabled: boolean = ballot?.new_features
    ? !ballot.new_features.enable_best_of_internal_analytics
    : true
  if (internalAnalyticsIsDisabled) {
    keenClient.recordEvent('page_view', {...ballotDetails, pagename})
  }

  const listOfAdViews: RecordedAd[] = []

  if (_.filter(displayAds, Boolean)?.length) {
    _.forEach(displayAds, ad => {
      const recordedAd: RecordedAd = {
        ad_id: ad?._id,
        ad_name: _.trim((ad as AdWithName)?.name),
        ad_view_type: 'DisplayAdView',
      }
      listOfAdViews.push(recordedAd)
      if (internalAnalyticsIsDisabled) {
        keenClient.recordEvent('ad_view', {...ballotDetails, ...recordedAd})
      }
    })
  }
  if (_.filter(footerAds, Boolean)?.length) {
    _.forEach(footerAds, ad => {
      const recordedAd: RecordedAd = {
        ad_id: ad?._id,
        ad_name: _.trim((ad as AdWithName)?.name),
        ad_view_type: 'FooterAdView',
      }
      listOfAdViews.push(recordedAd)
      if (internalAnalyticsIsDisabled) {
        keenClient.recordEvent('ad_view', {...ballotDetails, ...recordedAd})
      }
    })
  }
  if (destination?.type === 'ad') {
    const recordedAd: RecordedAd = {
      ad_id: destination._id,
      ad_name: _.trim(destination.ad_name),
      ad_view_type: 'InterstitialAdView',
    }
    listOfAdViews.push(recordedAd)
    if (internalAnalyticsIsDisabled) {
      keenClient.recordEvent('ad_view', {...ballotDetails, ...recordedAd})
    }
  }
  if (_.filter(sponsorshipAds, Boolean)?.length) {
    _.forEach(sponsorshipAds, ad => {
      const recordedAd: RecordedAd = {
        ad_id: ad?._id,
        ad_name: _.trim((ad as AdWithName)?.name),
        ad_view_type: 'SponsorshipAdView',
      }
      listOfAdViews.push(recordedAd)
      if (internalAnalyticsIsDisabled) {
        keenClient.recordEvent('ad_view', {...ballotDetails, ...recordedAd})
      }
    })
  }
  if (ballot?.new_features?.enable_best_of_internal_analytics) {
    const analyticData = {
      ballot_id: ballotId,
      category_id: group?._id || null,
      category_name: _.trim(group?.parent_category_name) || null,
      sub_category_id: destination?.type === 'question' ? destination._id : null,
      sub_category_name: destination?.type === 'question' ? _.trim(destination.name) : null,
      link_id: linkId || null,
      page_name: pagename,
      ad_views: listOfAdViews,
    }
    addAnalytic(analyticData)
  }
}
